import { Component } from '@theme/component';
import { clamp } from '@theme/utilities';

// Zoom tuning. Desktop = click-to-zoom target; touch pinch clamps to [MIN,MAX].
const Z_DESKTOP = 2.5;
const Z_TOUCH = 2;
const Z_MIN = 1;
const Z_MAX = 4;
const DRAG_THRESHOLD = 8; // px before a touch counts as a pan (vs a tap)
const CLICK_SUPPRESS_MS = 350; // swallow the click that trails a touch pan/pinch

/**
 * TRB product gallery.
 *
 * Mockup (docs/trb-design-system.md §6.5): a vertical thumbnail strip on the
 * LEFT, a large main image sitting on a `--trb-plinth` plinth in the centre, and
 * the remaining shots continuing in a stack BELOW the main image. On mobile the
 * thumbnails collapse to a horizontal row UNDER the main image.
 *
 * This is a deliberately small vanilla web component — no carousel library (per
 * CLAUDE.md). It does three things:
 *   1. swap the main image when a thumb is clicked / activated by keyboard,
 *      cross-fading the swap (fade out → set src → fade in, ~200ms),
 *   2. keep the active thumb in sync (aria-current + roving tabindex), and
 *   3. fade each continuation shot in once as it scrolls into view.
 *
 * The "continuation below" shots are plain server-rendered <img> with explicit
 * width/height + aspect-ratio (no CLS) and lazy loading — they need no JS, so
 * without this module the gallery still renders every image and remains fully
 * usable (the CSS reveals them by default; JS only adds the one quiet fade). The
 * component is therefore pure progressive enhancement.
 *
 * All motion is suppressed under `prefers-reduced-motion: reduce` — the swap is
 * instant and continuation shots are shown without a fade.
 *
 * Init is deferred until the gallery scrolls into view (IntersectionObserver),
 * matching the marquee pattern and CLAUDE.md's "init on visibility" rule.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} mainImage - The large <img> swapped on selection.
 * @property {HTMLButtonElement[]} thumbs - The thumbnail buttons.
 *
 * @extends Component<Refs>
 */
class TrbGallery extends Component {
  // No refs are *required*. This component is pure progressive enhancement —
  // every image is server-rendered with an explicit src/width/height, so the
  // gallery is fully usable even if a ref is missing. Declaring `mainImage` as
  // required (and then having the `image_tag` filter silently drop the `ref`
  // attribute) is exactly what threw a MissingRefError in connectedCallback and
  // stopped the PDP from painting. The markup now hard-codes `ref="mainImage"`,
  // and every access below is still individually guarded.
  requiredRefs = [];

  /** Defers component init until the gallery nears the viewport. */
  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** Fades each continuation shot in once it scrolls into view. */
  /** @type {IntersectionObserver | null} */
  #revealObserver = null;

  /** @type {boolean} */
  #ready = false;

  /** @type {MediaQueryList | null} */
  #reducedMotion = null;

  /** Index of the image currently shown in the zoom lightbox. @type {number} */
  #zoomIndex = 0;

  // ── Pan/zoom state ──────────────────────────────────────────────
  /** @type {boolean} */ #zoomed = false;
  /** @type {number} */ #zScale = 1;
  /** @type {number} */ #zx = 0;
  /** @type {number} */ #zy = 0;
  /** @type {number | null} */ #zoomRAF = null;
  /** @type {{ x: number, y: number } | null} */ #pendingPointer = null;
  /** @type {AbortController | null} */ #zoomGestures = null;
  /** @type {boolean} */ #dragging = false;
  /** @type {boolean} */ #didDrag = false;
  /** @type {{ x: number, y: number }} */ #dragStart = { x: 0, y: 0 };
  /** @type {{ x: number, y: number }} */ #dragStartTranslate = { x: 0, y: 0 };
  /** @type {Map<number, PointerEvent>} */ #activePointers = new Map();
  /** @type {number} */ #pinchStartDist = 0;
  /** @type {number} */ #pinchStartScale = 1;
  /** @type {number} */ #lastGestureEnd = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    // Signal JS presence so the CSS can hide continuation shots for the reveal.
    // Skipped under reduced motion so the shots are never hidden in that mode.
    if (!this.#reducedMotion.matches) this.classList.add('trb-gallery--js');

    // Defer wiring until the gallery is on (or near) screen.
    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.#init();
            this.#observer?.disconnect();
            this.#observer = null;
            break;
          }
        }
      },
      { rootMargin: '200px' }
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#revealObserver?.disconnect();
    this.#revealObserver = null;
    this.#zoomGestures?.abort();
    this.#zoomGestures = null;
    if (this.#zoomRAF != null) {
      cancelAnimationFrame(this.#zoomRAF);
      this.#zoomRAF = null;
    }
  }

  #init() {
    if (this.#ready) return;
    this.#ready = true;

    // Mark the first thumb active so keyboard focus + styling have a baseline.
    const active = this.#activeIndex();
    this.#setActive(active < 0 ? 0 : active, { focus: false, scroll: false });

    this.#initReveal();
  }

  /**
   * Fade each continuation shot in once as it scrolls into view (one quiet
   * fade). The CSS marks `[data-reveal]` items hidden and `.is-revealed` shown;
   * under reduced motion we simply reveal them all immediately with no observer.
   */
  #initReveal() {
    const items = /** @type {HTMLElement[]} */ (
      Array.from(this.querySelectorAll('[data-reveal]'))
    );
    if (items.length === 0) return;

    if (this.#reducedMotion?.matches) {
      for (const item of items) item.classList.add('is-revealed');
      return;
    }

    this.#revealObserver = new IntersectionObserver(
      (entries, observer) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-revealed');
            observer.unobserve(entry.target);
          }
        }
      },
      { rootMargin: '0px 0px -10% 0px' }
    );
    for (const item of items) this.#revealObserver.observe(item);
  }

  /**
   * Click handler wired declaratively: `on:click="/select/<index>"`.
   * @param {number} index
   */
  select(index) {
    this.#setActive(index, { focus: false, scroll: false });
  }

  /**
   * Keyboard handler wired declaratively: `on:keydown="/handleKey"`.
   * Arrow Up/Down (and Left/Right) move between thumbs; Home/End jump to ends.
   * @param {KeyboardEvent} event
   */
  handleKey(event) {
    const thumbs = this.#thumbList();
    if (thumbs.length === 0) return;

    const current = this.#activeIndex();
    let next = current;

    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        next = (current + 1) % thumbs.length;
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
        next = (current - 1 + thumbs.length) % thumbs.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = thumbs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.#setActive(next, { focus: true, scroll: true });
  }

  /* ── Zoom lightbox ──────────────────────────────────────────────
     Click the main image → open a full-screen <dialog> with the high-res
     (`data-zoom-src`) version, navigable across the set. Wired declaratively:
     main `on:click="/openZoom"`, buttons `/closeZoom` `/zoomPrev` `/zoomNext`,
     dialog `on:click="/onZoomBackdrop"` `on:keydown="/onZoomKey"`
     `on:close="/onZoomClose"`. Esc + focus-trap come free from showModal(). */

  /** Opens the lightbox at the currently-selected image. */
  openZoom() {
    const dialog = this.refs.zoomDialog;
    if (!(dialog instanceof HTMLDialogElement)) return;
    if (this.#zoomItems().length === 0) return;
    const active = this.#activeIndex();
    this.#setZoom(active < 0 ? 0 : active);
    this.#resetZoomTransform();
    dialog.showModal();
    this.#bindZoomGestures(dialog);
    // Quiet fade-in: flip the class on the next frame so the transition runs.
    if (this.#reducedMotion?.matches) dialog.classList.add('is-open');
    else requestAnimationFrame(() => dialog.classList.add('is-open'));
  }

  /** Closes the lightbox (teardown happens in onZoomClose for all close paths). */
  closeZoom() {
    const dialog = this.refs.zoomDialog;
    if (dialog instanceof HTMLDialogElement && dialog.open) dialog.close();
  }

  /** Fires for every close (×, Esc, backdrop, .close()) — reset everything. */
  onZoomClose() {
    this.#zoomGestures?.abort();
    this.#zoomGestures = null;
    if (this.#zoomRAF != null) {
      cancelAnimationFrame(this.#zoomRAF);
      this.#zoomRAF = null;
    }
    this.#activePointers.clear();
    this.#dragging = false;
    this.#exitZoom();
    this.refs.zoomDialog?.classList.remove('is-open');
    this.#setActive(this.#zoomIndex, { focus: false, scroll: true });
  }

  /** Wired to the prev/next buttons. Reset zoom before navigating. */
  zoomPrev() {
    this.#exitZoom();
    this.#setZoom(this.#zoomIndex - 1);
  }

  zoomNext() {
    this.#exitZoom();
    this.#setZoom(this.#zoomIndex + 1);
  }

  /** Wired to the lightbox thumbnail strip: jump straight to an image. */
  zoomTo(index) {
    this.#exitZoom();
    this.#setZoom(Number(index));
  }

  /**
   * Close only when the click is on the dialog backdrop itself. Image clicks run
   * onZoomImageClick (which stops propagation), so the two never collide.
   * @param {MouseEvent} event
   */
  onZoomBackdrop(event) {
    if (event.target === this.refs.zoomDialog) this.closeZoom();
  }

  /**
   * Click/tap the image → toggle zoom (fit ↔ 2.5×) at the point. The click that
   * trails a touch pan/pinch is swallowed (CLICK_SUPPRESS_MS) so panning doesn't
   * accidentally exit zoom.
   * @param {MouseEvent} event
   */
  onZoomImageClick(event) {
    event.stopPropagation();
    if (performance.now() - this.#lastGestureEnd < CLICK_SUPPRESS_MS) return;
    if (this.#zoomed) this.#exitZoom();
    else this.#enterZoom(event.clientX, event.clientY, Z_DESKTOP);
  }

  /**
   * Arrow keys navigate; Enter/Space on the image toggles zoom (keyboard). Esc is
   * handled natively by the modal dialog → onZoomClose resets.
   * @param {KeyboardEvent} event
   */
  onZoomKey(event) {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.zoomNext();
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.zoomPrev();
    } else if ((event.key === 'Enter' || event.key === ' ') && event.target === this.refs.zoomImage) {
      event.preventDefault();
      if (this.#zoomed) this.#exitZoom();
      else this.#enterZoom(null, null, Z_DESKTOP);
    }
  }

  /* ── pan/zoom internals ─────────────────────────────────────── */

  /**
   * Bind the non-delegated pointer events for the lifetime of an open lightbox
   * (Horizon's on: delegation doesn't cover pointermove/up/cancel). Torn down in
   * onZoomClose via abort().
   * @param {HTMLDialogElement} dialog
   */
  #bindZoomGestures(dialog) {
    this.#zoomGestures?.abort();
    this.#zoomGestures = new AbortController();
    const { signal } = this.#zoomGestures;
    dialog.addEventListener('pointerdown', this.#onZoomPointerDown, { signal });
    dialog.addEventListener('pointermove', this.#onZoomPointerMove, { signal, passive: false });
    dialog.addEventListener('pointerup', this.#onZoomPointerUp, { signal });
    dialog.addEventListener('pointercancel', this.#onZoomPointerUp, { signal });
    window.addEventListener('resize', this.#onZoomResize, { signal });
  }

  #onZoomResize = () => {
    if (this.#zoomed) this.#applyZoomTransform();
  };

  /**
   * Enter zoom, anchored at a screen point (or centered when null).
   * @param {number | null} clientX
   * @param {number | null} clientY
   * @param {number} scale
   */
  #enterZoom(clientX, clientY, scale) {
    const img = this.refs.zoomImage;
    if (!(img instanceof HTMLImageElement)) return;
    this.#zoomed = true;
    this.#zScale = scale;
    this.classList.add('trb-gallery--zoomed');
    img.setAttribute('aria-pressed', 'true');
    if (clientX == null || clientY == null) {
      this.#zx = 0;
      this.#zy = 0;
    } else {
      this.#panToPoint(clientX, clientY);
    }
    this.#applyZoomTransform();
  }

  /** Return to fit. */
  #exitZoom() {
    if (!this.#zoomed && this.#zScale === 1) return;
    this.#zoomed = false;
    this.#dragging = false;
    this.classList.remove('trb-gallery--zoomed', 'trb-gallery--panning');
    this.refs.zoomImage?.setAttribute('aria-pressed', 'false');
    this.#resetZoomTransform();
  }

  #resetZoomTransform() {
    this.#zScale = 1;
    this.#zx = 0;
    this.#zy = 0;
    const img = this.refs.zoomImage;
    if (img instanceof HTMLElement) {
      img.style.setProperty('--z-scale', '1');
      img.style.setProperty('--z-x', '0px');
      img.style.setProperty('--z-y', '0px');
    }
  }

  #applyZoomTransform() {
    this.#clampTranslate();
    const img = this.refs.zoomImage;
    if (!(img instanceof HTMLElement)) return;
    img.style.setProperty('--z-scale', String(this.#zScale));
    img.style.setProperty('--z-x', `${this.#zx}px`);
    img.style.setProperty('--z-y', `${this.#zy}px`);
  }

  /**
   * The contain-fit rendered size of the image within its layout box.
   * @returns {{ w: number, h: number, cw: number, ch: number } | null}
   */
  #renderedFit() {
    const img = this.refs.zoomImage;
    if (!(img instanceof HTMLImageElement)) return null;
    const cw = img.clientWidth;
    const ch = img.clientHeight;
    const nw = img.naturalWidth;
    const nh = img.naturalHeight;
    if (!cw || !ch || !nw || !nh) return null;
    const imgAR = nw / nh;
    const boxAR = cw / ch;
    let w;
    let h;
    if (imgAR > boxAR) {
      w = cw;
      h = cw / imgAR;
    } else {
      h = ch;
      w = ch * imgAR;
    }
    return { w, h, cw, ch };
  }

  /** Clamp scale + translate so the image can't pan past its edges. */
  #clampTranslate() {
    this.#zScale = clamp(this.#zScale, Z_MIN, Z_MAX);
    if (this.#zScale <= 1) {
      this.#zx = 0;
      this.#zy = 0;
      return;
    }
    const fit = this.#renderedFit();
    if (!fit) {
      this.#zx = 0;
      this.#zy = 0;
      return;
    }
    const maxX = Math.max(0, fit.w * this.#zScale - fit.cw) / 2;
    const maxY = Math.max(0, fit.h * this.#zScale - fit.ch) / 2;
    this.#zx = clamp(this.#zx, -maxX, maxX);
    this.#zy = clamp(this.#zy, -maxY, maxY);
  }

  /**
   * Map a pointer position over the visible image to a pan offset (moving toward
   * an edge reveals that edge).
   * @param {number} clientX
   * @param {number} clientY
   */
  #panToPoint(clientX, clientY) {
    const dialog = this.refs.zoomDialog;
    const fit = this.#renderedFit();
    if (!(dialog instanceof HTMLElement) || !fit) return;
    // Reference the STABLE dialog rect (full viewport), not the image's own rect —
    // the image rect moves as it pans, which would feed back into the mapping.
    const box = dialog.getBoundingClientRect();
    const fx = clamp((clientX - box.left) / (box.width || 1), 0, 1);
    const fy = clamp((clientY - box.top) / (box.height || 1), 0, 1);
    const maxX = Math.max(0, fit.w * this.#zScale - fit.cw) / 2;
    const maxY = Math.max(0, fit.h * this.#zScale - fit.ch) / 2;
    this.#zx = (0.5 - fx) * 2 * maxX;
    this.#zy = (0.5 - fy) * 2 * maxY;
  }

  /** Desktop hover-pan (fine pointer); touch is delegated to #onTouchMove. */
  #onZoomPointerMove = (event) => {
    if (event.pointerType === 'touch') {
      this.#onTouchMove(event);
      return;
    }
    if (!this.#zoomed) return;
    this.#pendingPointer = { x: event.clientX, y: event.clientY };
    this.classList.add('trb-gallery--panning');
    if (this.#zoomRAF == null) {
      this.#zoomRAF = requestAnimationFrame(() => {
        this.#zoomRAF = null;
        const p = this.#pendingPointer;
        if (p) this.#panToPoint(p.x, p.y);
        this.#applyZoomTransform();
      });
    }
  };

  /** Touch: track pointers; 2 → pinch, 1 (while zoomed, on the image) → drag. */
  #onZoomPointerDown = (event) => {
    if (event.pointerType !== 'touch') return;
    this.#activePointers.set(event.pointerId, event);
    if (this.#activePointers.size === 2) {
      this.#startPinch();
      return;
    }
    if (this.#zoomed && event.target === this.refs.zoomImage) {
      this.#dragging = true;
      this.#didDrag = false;
      this.#dragStart = { x: event.clientX, y: event.clientY };
      this.#dragStartTranslate = { x: this.#zx, y: this.#zy };
      // Capture so a drag that slides off the image keeps flowing. Guarded:
      // throws if the pointer isn't active (harmless, non-essential).
      try {
        this.refs.zoomImage?.setPointerCapture?.(event.pointerId);
      } catch {
        /* no active pointer — ignore */
      }
    }
  };

  /** @param {PointerEvent} event */
  #onTouchMove(event) {
    if (this.#activePointers.has(event.pointerId)) this.#activePointers.set(event.pointerId, event);
    if (this.#activePointers.size === 2) {
      event.preventDefault();
      this.classList.add('trb-gallery--panning');
      this.#processPinch();
      return;
    }
    if (!this.#dragging) return;
    event.preventDefault();
    const dx = event.clientX - this.#dragStart.x;
    const dy = event.clientY - this.#dragStart.y;
    if (!this.#didDrag && Math.hypot(dx, dy) > DRAG_THRESHOLD) {
      this.#didDrag = true;
      this.classList.add('trb-gallery--panning');
    }
    this.#zx = this.#dragStartTranslate.x + dx;
    this.#zy = this.#dragStartTranslate.y + dy;
    if (this.#zoomRAF == null) {
      this.#zoomRAF = requestAnimationFrame(() => {
        this.#zoomRAF = null;
        this.#applyZoomTransform();
      });
    }
  }

  /** @param {PointerEvent} event */
  #onZoomPointerUp = (event) => {
    const wasPinching = this.#activePointers.size >= 2;
    this.#activePointers.delete(event.pointerId);
    if (event.pointerType !== 'touch') return;

    if (this.#dragging && this.#activePointers.size === 0) {
      this.#dragging = false;
      this.classList.remove('trb-gallery--panning');
      // Only suppress the trailing click if it was a real pan (not a tap).
      if (this.#didDrag) this.#lastGestureEnd = performance.now();
      this.#applyZoomTransform();
    }

    if (wasPinching) {
      this.#pinchStartDist = 0;
      this.classList.remove('trb-gallery--panning');
      this.#lastGestureEnd = performance.now();
    }
  };

  #startPinch() {
    const [a, b] = [...this.#activePointers.values()];
    if (!a || !b) return;
    this.#dragging = false;
    this.#pinchStartDist = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY) || 1;
    this.#pinchStartScale = this.#zScale;
    if (!this.#zoomed) {
      this.#zoomed = true;
      this.classList.add('trb-gallery--zoomed');
      this.refs.zoomImage?.setAttribute('aria-pressed', 'true');
    }
  }

  #processPinch() {
    const [a, b] = [...this.#activePointers.values()];
    if (!a || !b || !this.#pinchStartDist) return;
    const dist = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
    this.#zScale = clamp((dist / this.#pinchStartDist) * this.#pinchStartScale, Z_MIN, Z_MAX);
    if (this.#zScale <= 1.02) this.#exitZoom();
    else this.#applyZoomTransform();
  }

  /**
   * The zoomable set: high-res src + alt per image, sourced from the thumbs
   * (or the lone main image when there are no thumbs).
   * @returns {{ src: string, alt: string }[]}
   */
  #zoomItems() {
    const thumbs = this.#thumbList();
    if (thumbs.length) {
      return thumbs.map((t) => ({
        src: t.dataset.zoomSrc || t.dataset.fullSrc || '',
        alt: t.dataset.fullAlt || '',
      }));
    }
    const main = this.refs.mainImage;
    if (main instanceof HTMLImageElement) {
      return [{ src: main.dataset.zoomSrc || main.src, alt: main.alt || '' }];
    }
    return [];
  }

  /**
   * Loads the image at `index` (wrapping) into the lightbox and updates the
   * counter. Guards against a mid-zoom src swap.
   * @param {number} index
   */
  #setZoom(index) {
    const items = this.#zoomItems();
    const n = items.length;
    if (n === 0) return;
    const i = ((index % n) + n) % n;
    this.#zoomIndex = i;
    if (this.#zoomed) this.#exitZoom();
    const img = this.refs.zoomImage;
    if (img instanceof HTMLImageElement) {
      img.src = items[i].src;
      img.alt = items[i].alt;
    }
    const counter = this.querySelector('.trb-gallery__zoom-index');
    if (counter) counter.textContent = String(i + 1);

    // Sync the lightbox thumbnail strip (active state + keep it in view).
    const zthumbs = this.querySelectorAll('.trb-gallery__zoom-thumb');
    zthumbs.forEach((t, ti) => t.setAttribute('aria-current', ti === i ? 'true' : 'false'));
    zthumbs[i]?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }

  /** @returns {HTMLButtonElement[]} */
  #thumbList() {
    const { thumbs } = this.refs;
    return Array.isArray(thumbs) ? thumbs : thumbs ? [thumbs] : [];
  }

  /** @returns {number} index of the currently active thumb, or -1. */
  #activeIndex() {
    return this.#thumbList().findIndex((t) => t.getAttribute('aria-current') === 'true');
  }

  /**
   * @param {number} index
   * @param {{ focus?: boolean, scroll?: boolean }} [options]
   */
  #setActive(index, { focus = false, scroll = false } = {}) {
    const thumbs = this.#thumbList();
    const thumb = thumbs[index];
    const main = this.refs.mainImage;
    if (!thumb || !(main instanceof HTMLImageElement)) return;

    const full = thumb.dataset.fullSrc;
    const srcset = thumb.dataset.fullSrcset;
    const alt = thumb.dataset.fullAlt;

    if (full && main.src !== full) {
      this.#swapMainImage(main, full, srcset, alt);
    }

    thumbs.forEach((t, i) => {
      const isActive = i === index;
      t.setAttribute('aria-current', isActive ? 'true' : 'false');
      // Roving tabindex: only the active thumb is in the tab order.
      t.tabIndex = isActive ? 0 : -1;
    });

    if (focus) thumb.focus();
    if (scroll) thumb.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }

  /**
   * Cross-fade the main image on swap: fade out → set src → fade in once the new
   * image has decoded. The first load is *not* a swap (the baseline image is
   * already painted), so this only runs on subsequent thumbnail selections.
   *
   * Under `prefers-reduced-motion` we set the src directly with no fade. The
   * `.is-swapping` class drives opacity:0 in CSS (transition: opacity ~200ms
   * --trb-ease-out-quad), so the timing/easing live in tokens, not JS.
   *
   * @param {HTMLImageElement} main
   * @param {string} full
   * @param {string | undefined} srcset
   * @param {string | undefined} alt
   */
  #swapMainImage(main, full, srcset, alt) {
    const apply = () => {
      main.src = full;
      if (srcset) main.srcset = srcset;
      if (alt != null) main.alt = alt;
    };

    if (this.#reducedMotion?.matches) {
      apply();
      return;
    }

    // Fade out → swap src (after the fade-out completes) → decode → fade in.
    // A token guards overlapping swaps so a faster second click wins cleanly.
    const token = (this.#swapToken = this.#swapToken + 1);

    const reveal = () => {
      if (token !== this.#swapToken) return;
      apply();
      const finish = () => {
        if (token === this.#swapToken) main.classList.remove('is-swapping');
      };
      // Decode the new image before fading it in so we never reveal a
      // half-painted frame. `decode()` may reject (e.g. cache miss) — fall back.
      main.decode?.().then(finish, finish) ?? finish();
    };

    // Trigger the fade-out, then swap once it has actually finished. `transitionend`
    // gives the true ~200ms out; a timeout is the safety net if it never fires
    // (e.g. tab hidden, opacity already 0). The shorter of the two wins via token.
    let swapped = false;
    const onFadedOut = () => {
      if (swapped || token !== this.#swapToken) return;
      swapped = true;
      main.removeEventListener('transitionend', onTransitionEnd);
      reveal();
    };
    const onTransitionEnd = (/** @type {TransitionEvent} */ event) => {
      if (event.target === main && event.propertyName === 'opacity') onFadedOut();
    };
    main.addEventListener('transitionend', onTransitionEnd);
    setTimeout(onFadedOut, 260);

    // Add the class on the next frame so the transition fires from opacity:1.
    requestAnimationFrame(() => main.classList.add('is-swapping'));
  }

  /** @type {number} */
  #swapToken = 0;
}

if (!customElements.get('trb-gallery')) {
  customElements.define('trb-gallery', TrbGallery);
}
